'use client';

import '@/features/cards/tokens';

import { useToggleBookmark } from '@/api/mutations/video.mutations';
import { useBookmarkOverrides } from '@/api/queries/bookmark-overrides.queries';
import { usePreviewShaka } from '@/features/video/hooks/use-preview-shaka';
import {
  VideoCard,
  VideoDiscoveryCard,
  VideoDiscoveryCardCompact,
  type VideoCardData,
} from '@/features/cards';
import type { FormatTone } from '@/features/cards/primitives/BadgeStack';
import type { ContentFlagKind } from '@/features/cards/primitives/ContentFlagChip';
import { PLAYER_CONFIG } from '@/features/video/constants/player-constants';
import { shareVideoLink } from '@/features/video/lib/share-video';
import { buildBunnyOverlay } from '@/components/video/bunny-hover-preview';
import {
  readPersistentPlayer,
  setOptimisticPlayerVideo,
} from '@/features/video/stores/persistent-player-store';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { useAuthStore } from '@/shared/stores/auth-store';
import posthog from 'posthog-js';
import { usePrefetchVideo } from '@/shared/hooks/use-prefetch-video';
import { captureError } from '@/shared/lib/errors/capture';
import { CARD_IMAGE_SIZES } from '@/shared/constants/card-image-sizes';
import { resolveVideoCountryIso } from '@/shared/utils/country-iso';
import { getChannelHandle, getCreatorRoute } from '@/shared/utils/formatting';
import type { Video } from '@/types';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

const SWAP_DEDUPE_MS = 2000;

interface VideoCardContainerProps {
  video: Video;
  onOpenPreview?: (video: Video) => void;
  eager?: boolean;
  sizes?: string;
  /**
   * `default` renders plain VideoCard (locator + title body).
   * `discovery` renders VideoDiscoveryCard (title + channel row +
   * time-ago body) — used on home carousels that surface creator
   * attribution.
   * `discovery-compact` renders the horizontal YouTube-sidebar layout
   * — thumb left, title + channel + time-ago right. No hover preview.
   */
  variant?: 'default' | 'discovery' | 'discovery-compact';
  /**
   * Force the card into the locked state (LockCorner + Save hidden +
   * no hover preview). Use on freemium gates where the viewer can't
   * access the video regardless of its own `is_free` flag.
   */
  locked?: boolean;
  /**
   * Override the adapter's computed href (default `/videos/<uuid>`).
   * Used for freemium gates to route to `/choose-plan` instead.
   */
  href?: string;
}

const NEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const CARD_BY_VARIANT = {
  default: VideoCard,
  discovery: VideoDiscoveryCard,
  'discovery-compact': VideoDiscoveryCardCompact,
} as const;

const DEFAULT_SIZES_BY_VARIANT = {
  default: CARD_IMAGE_SIZES.landscape,
  discovery: CARD_IMAGE_SIZES.landscape,
  'discovery-compact': CARD_IMAGE_SIZES.compact,
} as const;

function deriveFormat(video: Video): { format?: string; formatTone?: FormatTone } {
  if (video.latest === true) {
    return { format: 'LATEST' };
  }
  const isNew =
    typeof video.published_at === 'string'
      ? new Date(video.published_at).getTime() > Date.now() - NEW_WINDOW_MS
      : false;
  if (isNew) {
    return { format: 'NEW', formatTone: 'new' };
  }
  return {};
}

// API surfaces channel profile picture at inconsistent paths across
// endpoints. Widen `Video` with the shapes RailCard historically
// consumed and resolve in order: channel.dp → creator.dp → creator.channel.dp
// → user.dp → user.small_dp.
function deriveChannel(video: Video): VideoCardData['channel'] {
  const name = video.channel?.name;
  if (name === undefined || name.length === 0) return undefined;

  const videoAny = video as Video & {
    creator?: {
      dp?: string;
      verified?: boolean;
      handler?: string;
      channel?: { dp?: string; handler?: string };
    };
    user?: { dp?: string; small_dp?: string; handler?: string };
  };
  const avatarSrc =
    video.channel?.dp ??
    videoAny.creator?.dp ??
    videoAny.creator?.channel?.dp ??
    videoAny.user?.dp ??
    videoAny.user?.small_dp;
  const handler =
    getChannelHandle(video.channel) ??
    videoAny.creator?.handler ??
    getChannelHandle(videoAny.creator?.channel) ??
    videoAny.user?.handler;
  const href =
    typeof handler === 'string' && handler.length > 0 ? getCreatorRoute(handler) : undefined;
  const verified =
    (video.channel as { verified?: boolean } | undefined)?.verified ?? videoAny.creator?.verified;

  return {
    name,
    ...(avatarSrc !== undefined && avatarSrc.length > 0 ? { avatarSrc } : {}),
    ...(href !== undefined ? { href } : {}),
    ...(verified !== undefined ? { verified } : {}),
  };
}

function deriveProgress(video: Video): VideoCardData['progress'] {
  const up = video.user_progress;
  if (up === null || up === undefined) return undefined;
  if (up.completed === true) return undefined;
  if (typeof up.duration !== 'number' || up.duration <= 0) return undefined;
  if (typeof up.position !== 'number' || up.position <= 0) return undefined;
  return { position: up.position, duration: up.duration };
}

function deriveFlags(video: Video): ContentFlagKind[] {
  const flags: ContentFlagKind[] = [];
  if (video.is_adult_content === true) flags.push('sensitive');
  if (video.is_substance_content === true) flags.push('substance');
  if (video.is_exclusive_content === true) flags.push('exclusive');
  if (video.has_4k === true) flags.push('4k');
  return flags;
}

function adaptVideo(video: Video): VideoCardData {
  const thumbnail = video.thumbnail_webp ?? video.thumbnail ?? video.card_thumbnail ?? '';
  const title = video.title ?? '';
  const { format, formatTone } = deriveFormat(video);
  const flags = deriveFlags(video);
  const country = resolveVideoCountryIso(video);
  const place = video.location ?? undefined;
  const channel = deriveChannel(video);
  const publishedAt = [video.published_at, video.created_at].find(
    (v): v is string => typeof v === 'string' && v.length > 0
  );
  const progress = deriveProgress(video);

  return {
    href: `/videos/${video.uuid ?? video.id}`,
    thumbnailSrc: thumbnail,
    thumbnailAlt: title || 'Video',
    title,
    ...(video.duration !== undefined && video.duration > 0 ? { duration: video.duration } : {}),
    ...(format !== undefined ? { format } : {}),
    ...(formatTone !== undefined ? { formatTone } : {}),
    ...(flags.length > 0 ? { flags } : {}),
    ...(country !== undefined ? { country } : {}),
    ...(place !== undefined ? { place } : {}),
    ...(channel !== undefined ? { channel } : {}),
    ...(publishedAt !== undefined ? { publishedAt } : {}),
    ...(progress !== undefined ? { progress } : {}),
  };
}

export function VideoCardContainer({
  video,
  onOpenPreview,
  eager,
  sizes,
  variant = 'default',
  locked,
  href,
}: VideoCardContainerProps): ReactElement {
  const cardData = useMemo(() => {
    const base = adaptVideo(video);
    return {
      ...base,
      ...(href !== undefined ? { href } : {}),
      ...(locked === true ? { locked: true } : {}),
    };
  }, [video, href, locked]);

  const toggleBookmark = useToggleBookmark();
  const overrides = useBookmarkOverrides();
  const override = video.uuid !== undefined ? overrides[video.uuid] : undefined;
  const saved = override ?? video.is_bookmarked ?? video.in_watchlist ?? false;

  const prefetchVideo = usePrefetchVideo();

  const handleToggleBookmark = useCallback((): void => {
    if (video.uuid === undefined) return;
    toggleBookmark.mutate({
      videoUuid: video.uuid,
      videoId: video.id,
      currentlyBookmarked: saved,
    });
  }, [video.uuid, video.id, saved, toggleBookmark]);

  const handlePrefetch = useCallback((): void => {
    const videoIdStr = String(video.uuid ?? video.id);
    if (videoIdStr.length === 0) return;
    const hlsUrl = video.hls_url ?? video.url_hls ?? video.bunny_hls_url ?? null;
    prefetchVideo(videoIdStr, hlsUrl);
  }, [video.uuid, video.id, video.hls_url, video.url_hls, video.bunny_hls_url, prefetchVideo]);

  const handleOpenPreview = useCallback((): void => {
    onOpenPreview?.(video);
  }, [onOpenPreview, video]);

  const isAuthenticated = useAuthStore((s) => !!s.user);

  // Anonymous viewers can't bookmark — the card shows Share instead.
  const handleShare = useCallback((): void => {
    shareVideoLink({
      videoKey: video.uuid ?? video.id,
      videoUuid: video.uuid ?? null,
      title: video.title,
      source: 'card',
      isAuthenticated,
      component: 'VideoCardContainer',
    });
  }, [video.uuid, video.id, video.title, isAuthenticated]);

  // Rage-clicking a card during a slow route transition re-fires the swap
  // capture with identical payloads (observed 14× in one session, FRONT-272);
  // repeat clicks within the window are dropped — $rageclick autocapture
  // still records the frustration signal.
  const lastSwapRef = useRef<{ uuid: string | null; at: number }>({ uuid: null, at: 0 });

  const handleNavigate = useCallback((): void => {
    const toUuid = video.uuid ?? null;
    const now = Date.now();
    if (lastSwapRef.current.uuid === toUuid && now - lastSwapRef.current.at < SWAP_DEDUPE_MS) {
      return;
    }
    lastSwapRef.current = { uuid: toUuid, at: now };

    // Feed the layout-mounted <PersistentPlayer> at click time, closing the
    // hydration gap where the player keeps rendering the prior video until
    // <WatchPlayDataHydrator> runs post page-server-render.
    const fromVideo = readPersistentPlayer().video;
    const hlsUrl = video.hls_url ?? video.url_hls ?? video.bunny_hls_url ?? null;
    setOptimisticPlayerVideo(video);
    posthog.capture(AnalyticsEvent.VIDEO_SWAP_INITIATED, {
      from_uuid: fromVideo?.uuid ?? null,
      to_uuid: toUuid,
      had_hls_url: hlsUrl !== null,
      source: 'related_card',
      is_authenticated: isAuthenticated,
    });
  }, [video, isAuthenticated]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const previewUrl = video.url_480 ?? video.url_720;
  const hlsUrl = video.hls_url ?? video.url_hls ?? video.bunny_hls_url ?? null;
  const bunnyVideoId = video.bunny_video_id;
  // Bunny `preview.webp` is the preferred hover preview whenever available.
  // It's a single lightweight webp (not playback), so locked cards keep it
  // too — teasing the content is intentional on freemium gates. Shaka, in
  // contrast, is gated on `!locked` below since it starts real playback.
  const wantsBunny = typeof bunnyVideoId === 'string' && bunnyVideoId.length > 0;

  const {
    play: shakaPlay,
    stop: shakaStop,
    isReady: shakaReady,
  } = usePreviewShaka(videoRef, hlsUrl);

  const previewReady = hlsUrl !== null ? shakaReady : isVideoReady;
  const showPreview = isVideoPlaying && previewReady;

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current !== null) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const handleHoverStart = useCallback((): void => {
    // Bunny webp handles its own hover fade via CSS — no JS playback to kick off.
    if (wantsBunny) return;
    hoverTimeoutRef.current = setTimeout(() => {
      if (hlsUrl !== null) {
        setIsVideoPlaying(true);
        shakaPlay();
        return;
      }
      if (previewUrl !== undefined && videoRef.current !== null) {
        videoRef.current.currentTime = 0;
        videoRef.current
          .play()
          .then(() => setIsVideoPlaying(true))
          .catch((err: unknown) => {
            if (err instanceof DOMException && err.name === 'AbortError') return;
            captureError(err, {
              component: 'VideoCardContainer',
              action: 'autoplay-preview',
              severity: 'info',
              skipPostHog: true,
            });
          });
      }
    }, PLAYER_CONFIG.HOVER_PREVIEW.HOVER_DELAY);
  }, [wantsBunny, hlsUrl, previewUrl, shakaPlay]);

  const handleHoverEnd = useCallback((): void => {
    if (wantsBunny) return;
    setIsVideoPlaying(false);
    if (hoverTimeoutRef.current !== null) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (hlsUrl !== null) {
      shakaStop();
      return;
    }
    if (videoRef.current !== null) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [wantsBunny, hlsUrl, shakaStop]);

  const wantsShaka =
    !wantsBunny &&
    variant !== 'discovery-compact' &&
    locked !== true &&
    (hlsUrl !== null || previewUrl !== undefined);
  // Extracted out of JSX so sonarjs/no-nested-conditional doesn't fire on
  // a conditional-spread nested inside the outer ternary.
  const mp4Attrs: { loop: true; preload: 'none' } | Record<string, never> =
    hlsUrl === null ? { loop: true, preload: 'none' } : {};
  const bunnyOverlay = buildBunnyOverlay(wantsBunny, bunnyVideoId);
  const shakaOverlay: ReactElement | null = wantsShaka ? (
    <video
      ref={videoRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: showPreview ? 1 : 0,
        transition: 'opacity 300ms ease',
      }}
      muted
      playsInline
      {...mp4Attrs}
      onPlaying={() => setIsVideoReady(true)}
    >
      {hlsUrl === null && previewUrl !== undefined ? (
        <source src={previewUrl} type="video/mp4" />
      ) : null}
    </video>
  ) : null;
  const previewOverlay: ReactElement | null = bunnyOverlay ?? shakaOverlay;

  const Card = CARD_BY_VARIANT[variant];

  return (
    <Card
      video={cardData}
      isBookmarked={saved}
      {...(isAuthenticated ? { onToggleBookmark: handleToggleBookmark } : { onShare: handleShare })}
      onPrefetch={handlePrefetch}
      onNavigate={handleNavigate}
      {...(onOpenPreview !== undefined ? { onOpenPreview: handleOpenPreview } : {})}
      onHoverStart={handleHoverStart}
      onHoverEnd={handleHoverEnd}
      {...(previewOverlay !== null ? { previewOverlay } : {})}
      {...(eager !== undefined ? { eager } : {})}
      sizes={sizes ?? DEFAULT_SIZES_BY_VARIANT[variant]}
    />
  );
}
